/**
 * Thin wrapper around the Next.js reports proxy endpoints. Uses a bare
 * same-origin `fetch` (not the project-wide `apiFetch`) and relies on the
 * browser sending the session cookie automatically — every caller targets the
 * Next proxy on the same origin, which forwards auth to Laravel server-side.
 */
import { z } from "zod";

import { Audience, ReportMode } from "../schema";
import type { ReportSpec } from "../schema";

/** Schema-derived literal unions so `mode`/`audience` aren't loose strings. */
export type ReportModeValue = z.infer<typeof ReportMode>;
export type AudienceValue = z.infer<typeof Audience>;

/**
 * Payload accepted by `reportsApi.update` (PUT /api/reports/:id). Shared with
 * the `useUpdateReport` mutation hook so the editor + client agree on the
 * exact shape — `mode`/`audience` are constrained to the schema enums rather
 * than `string`.
 */
export interface UpdateReportInput {
  name?: string;
  mode?: ReportModeValue;
  audience?: AudienceValue;
  status?: "active" | "archived" | "trashed";
  spec?: ReportSpec;
  author_agent?: string;
}

export interface ReportSummary {
  id: string;
  name: string;
  mode: string;
  audience: string;
  is_template: boolean;
  status: "active" | "archived" | "trashed";
  current_version: number;
  updated_at: string;
}

export interface ReportDetail extends ReportSummary {
  spec: ReportSpec;
  parent_template_id: string | null;
  user_id: number;
  recruiter_id: number | null;
  created_at: string;
}

interface FetchOptions extends RequestInit {
  query?: Record<string, string | number | boolean | undefined>;
}

async function call<T>(path: string, options: FetchOptions = {}): Promise<T> {
  // Build a query string against the path; we never need an absolute URL here
  // because every caller targets the Next proxy on the same origin.
  let target = path;
  if (options.query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(options.query)) {
      if (v !== undefined) params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) target += (target.includes("?") ? "&" : "?") + qs;
  }
  const res = await fetch(target, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(options.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Reports API ${res.status}: ${body || res.statusText}`);
  }

  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

export const reportsApi = {
  list: async (params?: {
    include_templates?: boolean;
    templates_only?: boolean;
    mode?: string;
  }): Promise<ReportSummary[]> =>
    (await call<{ data: ReportSummary[] }>("/api/reports", { query: params })).data,

  get: async (id: string): Promise<ReportDetail> =>
    (await call<{ data: ReportDetail }>(`/api/reports/${id}`)).data,

  create: async (input: {
    name: string;
    mode?: string;
    audience?: string;
    spec: ReportSpec;
    is_template?: boolean;
  }): Promise<ReportDetail> =>
    (
      await call<{ data: ReportDetail }>("/api/reports", {
        method: "POST",
        body: JSON.stringify(input),
      })
    ).data,

  update: async (
    id: string,
    input: UpdateReportInput,
  ): Promise<ReportDetail> =>
    (
      await call<{ data: ReportDetail }>(`/api/reports/${id}`, {
        method: "PUT",
        body: JSON.stringify(input),
      })
    ).data,

  remove: async (id: string): Promise<ReportDetail> =>
    (
      await call<{ data: ReportDetail }>(`/api/reports/${id}`, {
        method: "DELETE",
      })
    ).data,

  duplicate: async (id: string): Promise<ReportDetail> =>
    (
      await call<{ data: ReportDetail }>(`/api/reports/${id}/duplicate`, {
        method: "POST",
      })
    ).data,

  versions: async (id: string): Promise<Array<{ version: number; created_at: string; author_agent?: string; semantic_diff?: { summary?: string } }>> =>
    (await call<{ data: Array<{ version: number; created_at: string; author_agent?: string; semantic_diff?: { summary?: string } }> }>(`/api/reports/${id}/versions`)).data,

  restore: async (id: string, version: number): Promise<ReportDetail> =>
    (
      await call<{ data: ReportDetail }>(`/api/reports/${id}/versions/${version}/restore`, {
        method: "POST",
      })
    ).data,

  wizardHealth: async (since?: string): Promise<WizardHealthSnapshot> =>
    (await call<{ data: WizardHealthSnapshot }>("/api/reports/wizard/health", {
      query: since ? { since } : undefined,
    })).data,
};

export interface WizardHealthSnapshot {
  since: string;
  totals: {
    invoked: number;
    accepted: number;
    rejected: number;
    errors: number;
  };
  accept_rate: number | null;
  error_rate: number | null;
  latency_ms: {
    p50: number | null;
    p95: number | null;
    samples: number;
  };
  block_type_mix: Array<{ block_type: string; count: number }>;
  eval: {
    passed: number;
    failed: number;
    pass_rate: number | null;
    recent_failures: Array<{
      occurred_at: string;
      id: string | null;
      prompt: string | null;
      failures: string[];
    }>;
    prompt_suggestions: Array<{
      category: string;
      priority: "high" | "medium" | "low";
      suggestion: string;
      count: number;
    }>;
  };
}
